import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getRegionProfile } from '../config/region';
import type { LocaleCode } from '../config/region/types';

const profile = getRegionProfile();

// 所有语言元数据
const ALL_LANGUAGES = [
  { code: 'zh-CN' as LocaleCode, name: '简体中文', flag: '🇨🇳' },
  { code: 'en-US' as LocaleCode, name: 'English', flag: '🇺🇸' },
  { code: 'ja-JP' as LocaleCode, name: '日本語', flag: '🇯🇵' },
] as const;

/** 当前区域允许的语言列表 */
export const SUPPORTED_LANGUAGES = ALL_LANGUAGES.filter((l) =>
  (profile.supportedLocales as readonly LocaleCode[]).includes(l.code)
);

export type LanguageCode = (typeof ALL_LANGUAGES)[number]['code'];

interface LanguageState {
  language: LanguageCode;
  setLanguage: (language: LanguageCode) => void;
  // 标记用户是否明确选择过语言（用于区分默认 vs 用户选择）
  hasUserSelected: boolean;
}

/** 确保初始语言在当前区域支持范围内 */
function clampLocale(code: string): LanguageCode {
  const found = SUPPORTED_LANGUAGES.find((l) => l.code === code);
  return found ? found.code : (profile.defaultLocale as LanguageCode);
}

// 根据区域生成 storage key，确保切换区域时重置语言设置
const STORAGE_KEY = `language-storage-${profile.region}`;

export const useLanguageStore = create<LanguageState>()(
  persist(
    (set) => ({
      language: profile.defaultLocale as LanguageCode,
      hasUserSelected: false,
      setLanguage: (language) => set({ 
        language: clampLocale(language),
        hasUserSelected: true,
      }),
    }),
    {
      name: STORAGE_KEY,
      // 恢复持久化时，确保值在区域允许范围内
      merge: (persisted, current) => {
        const p = persisted as Partial<LanguageState>;
        // 如果持久化的语言不在当前区域支持列表中，使用默认语言
        const lang = p?.language ? clampLocale(p.language) : current.language;
        return { 
          ...current, 
          language: lang,
          hasUserSelected: p?.hasUserSelected ?? false,
        };
      },
    }
  )
);
